/**
 * PeriodSelector
 * Compact Linear-style period picker for the PageToolbar tabs slot.
 * Layout: [◄] label ▾ [►]
 * Clicking the label opens a Popover with periods grouped by year.
 */

import { useRef, useEffect } from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  Popover,
  MenuList,
  MenuItem,
  Typography,
  Button,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import type { PotPeriodSummaryDto } from '../api/potPeriodsApi';

interface PeriodsByYear {
  year: number;
  periods: PotPeriodSummaryDto[];
}

interface PeriodSelectorProps {
  label: string;
  shortLabel: string;
  periodsByYear: PeriodsByYear[];
  selection: {
    effectivePeriodId: string | null;
    onPeriodChange: (periodId: string) => void;
  };
  navigation: {
    onPrev: () => void;
    onNext: () => void;
    canGoPrev: boolean;
    canGoNext: boolean;
  };
  pagination?: {
    hasMoreYears?: boolean;
    onLoadMoreYears?: () => void;
  };
}

export function PeriodSelector({
  label,
  shortLabel,
  periodsByYear,
  selection,
  navigation,
  pagination,
}: PeriodSelectorProps) {
  const { effectivePeriodId, onPeriodChange } = selection;
  const { onPrev, onNext, canGoPrev, canGoNext } = navigation;
  const { hasMoreYears, onLoadMoreYears } = pagination ?? {};

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const selectedItemRef = useRef<HTMLLIElement>(null);

  // Auto-scroll to selected period when popover opens
  useEffect(() => {
    if (open && selectedItemRef.current) {
      // Small delay to ensure popover has rendered
      requestAnimationFrame(() => {
        selectedItemRef.current?.scrollIntoView({
          block: 'center',
          behavior: 'instant',
        });
      });
    }
  }, [open]);

  const handleSelect = (periodId: string) => {
    onPeriodChange(periodId);
    setOpen(false);
  };

  return (
    <Box
      data-testid="period-selector"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: -0.5 }}
    >
      <IconButton
        size="small"
        onClick={onPrev}
        disabled={!canGoPrev}
        aria-label="Previous period"
        data-testid="period-selector-prev"
        sx={{ p: 0.5, transition: 'all 0.2s ease' }}
      >
        <ChevronLeftIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen(true)}
        data-testid="period-selector-toggle"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          transition: 'all 0.2s ease',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {/* Full label on sm+, short on xs */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            display: { xs: 'block', sm: 'none' },
          }}
        >
          {shortLabel}
        </Typography>
        <ArrowDropDownIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      </ButtonBase>

      <IconButton
        size="small"
        onClick={onNext}
        disabled={!canGoNext}
        aria-label="Next period"
        data-testid="period-selector-next"
        sx={{ p: 0.5, transition: 'all 0.2s ease' }}
      >
        <ChevronRightIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              maxHeight: 360,
              minWidth: 280,
              overflow: 'auto',
            },
          },
        }}
      >
        <MenuList dense data-testid="period-selector-popover">
          {periodsByYear.map(({ year, periods }) => (
            <Box key={year}>
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  fontWeight: 600,
                  color: 'text.secondary',
                  letterSpacing: 0.5,
                  px: 2,
                  pt: 1.5,
                  pb: 0.5,
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1,
                }}
              >
                {year}
              </Typography>
              {periods.map(period => {
                const isSelected = period.id === effectivePeriodId;
                return (
                  <MenuItem
                    key={period.id}
                    ref={isSelected ? selectedItemRef : undefined}
                    selected={isSelected}
                    onClick={() => handleSelect(period.id)}
                    data-testid={`period-selector-menuitem-${period.id}`}
                    sx={{ typography: 'body2', py: 0.75 }}
                  >
                    {period.displayLabel}
                  </MenuItem>
                );
              })}
            </Box>
          ))}
          {hasMoreYears && onLoadMoreYears && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={onLoadMoreYears}
                data-testid="period-selector-load-more"
                sx={{ typography: 'caption', textTransform: 'none' }}
              >
                Load older years...
              </Button>
            </Box>
          )}
        </MenuList>
      </Popover>
    </Box>
  );
}
